/**
 * The marks after a node's name, in the node list and on the map (nobgp-router#2010): where the
 * machine runs, then Tailscale.
 *
 * - `grok`: the machine is a sandbox VM of xAI's Grok Bot (`node_metadata.bot`, which the node
 *   detects).
 * - `nobgp`: noBGP provisioned it (an open compute task, the same fact `useComputeNodes` gives).
 * - `aws`: the machine is an AWS EC2 instance (`node_metadata.cloud`, which the node reads from
 *   its own firmware). ⚠ Never from the address's AS: `as_org` is MaxMind data the app does not
 *   select (nobgp-router#1934, `nodeMetadataColumns.ts`).
 * - `tailscale`: Tailscale is RUNNING on it. Installed and not running shows nothing.
 *
 * ⚠ ONE place mark, in that order: a node noBGP provisioned is at AWS too, and the cloud says
 * more. A node the router knows nothing about has no mark, never a guess.
 */
export type NodeMark = 'grok' | 'nobgp' | 'aws' | 'tailscale'

export type NodeMarkFacts = {
  bot?: string | null
  tailscale?: string | null
  cloud?: string | null
  provisioned?: boolean
}

export const NODE_MARK_TITLES: Record<NodeMark, string> = {
  grok: 'Grok Bot',
  nobgp: 'noBGP cloud',
  aws: 'AWS',
  tailscale: 'Tailscale is running',
}

/**
 * The hover line that names a node's marks on the graph and the map, where an icon has no tooltip
 * of its own (it takes no pointer). `marks` is `GraphNode.marks`, comma-joined. The noBGP cloud
 * is left out: its node's hover already carries the compute line. undefined for none.
 */
export function marksTitle(marks: string | undefined): string | undefined {
  const names = (marks ?? '')
    .split(',')
    .filter((mark): mark is NodeMark => mark in NODE_MARK_TITLES && mark !== 'nobgp')
    .map(mark => NODE_MARK_TITLES[mark])
  return names.length ? names.join(' · ') : undefined
}

export function nodeMarks({ bot, tailscale, cloud, provisioned }: NodeMarkFacts): NodeMark[] {
  const marks: NodeMark[] = []
  if (bot === 'grok') marks.push('grok')
  else if (provisioned) marks.push('nobgp')
  else if (cloud === 'aws') marks.push('aws')
  if (tailscale === 'running') marks.push('tailscale')
  return marks
}

/**
 * The cloud a node is drawn in, in place of its place (`CloudPlace`; Benoit, 2026-10-20): the
 * place mark when it is Grok Bot or the noBGP cloud. AWS is a place mark too, and is not a cloud
 * location: an AWS machine is drawn at its region.
 */
export function cloudPlaceOf(marks: NodeMark[]): CloudPlace | undefined {
  return marks.find((mark): mark is CloudPlace => mark === 'grok' || mark === 'nobgp')
}

/** The id of a cloud location in the graph: one for each cloud, for every network. */
export const cloudPlaceId = (place: CloudPlace) => `cloud:${place}`
